import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  GraduationCap,
  Eye,
  EyeOff,
  Loader2,
  Car,
  ArrowLeft,
  ArrowRight,
  Check,
} from 'lucide-react'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'

import { supabase } from '@/lib/supabase'
import { track } from '@/lib/analytics'
import { useCpfValidation, maskCpf } from '@/hooks/useCpfValidation'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

// ─── Validation schema ────────────────────────────────────────────────────────

const registerSchema = z
  .object({
    full_name: z
      .string()
      .min(3, 'Nome deve ter pelo menos 3 caracteres')
      .max(100),
    email: z.string().email('E-mail inválido'),
    phone: z
      .string()
      .min(10, 'Telefone inválido')
      .max(15)
      .regex(/^[\d\s()\-+]+$/, 'Formato de telefone inválido'),
    password: z
      .string()
      .min(8, 'Senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Z]/, 'Deve conter pelo menos uma letra maiúscula')
      .regex(/[0-9]/, 'Deve conter pelo menos um número'),
    confirmPassword: z.string(),
    acceptTerms: z
      .boolean()
      .refine((v) => v === true, {
        message: 'Você precisa aceitar os Termos de Uso e a Política de Privacidade',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  })

type RegisterFormData = z.infer<typeof registerSchema>
type Role = 'student' | 'instructor'

// ─── Role card component ──────────────────────────────────────────────────────

interface RoleCardProps {
  role: Role
  selected: boolean
  onSelect: (role: Role) => void
}

function RoleCard({ role, selected, onSelect }: RoleCardProps) {
  const isStudent = role === 'student'

  return (
    <button
      type="button"
      onClick={() => onSelect(role)}
      className={`
        relative flex flex-col items-center gap-4 rounded-2xl border-2 p-8 text-center
        transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-2
        focus:ring-offset-2
        ${
          selected
            ? isStudent
              ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500 ring-offset-2'
              : 'border-teal-500 bg-teal-50 ring-2 ring-teal-500 ring-offset-2'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
        }
      `}
    >
      {selected && (
        <div
          className={`absolute right-3 top-3 rounded-full p-0.5 ${
            isStudent ? 'bg-brand-500' : 'bg-teal-500'
          }`}
        >
          <Check className="h-3.5 w-3.5 text-white" />
        </div>
      )}

      <div
        className={`rounded-2xl p-4 ${
          selected
            ? isStudent
              ? 'bg-brand-500 text-white'
              : 'bg-teal-500 text-white'
            : 'bg-gray-100 text-gray-500'
        }`}
      >
        {isStudent ? (
          <GraduationCap className="h-10 w-10" />
        ) : (
          <Car className="h-10 w-10" />
        )}
      </div>

      <div>
        <p
          className={`text-lg font-bold ${
            selected
              ? isStudent
                ? 'text-brand-700'
                : 'text-teal-700'
              : 'text-gray-800'
          }`}
        >
          {isStudent ? 'Sou aluno' : 'Sou instrutor'}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          {isStudent
            ? 'Quero aprender a dirigir com os melhores'
            : 'Quero oferecer aulas e aumentar minha renda'}
        </p>
      </div>
    </button>
  )
}

// ─── Progress indicator ───────────────────────────────────────────────────────

function ProgressIndicator({ currentStep }: { currentStep: 1 | 2 }) {
  return (
    <div className="flex items-center justify-center gap-3">
      {[1, 2].map((step) => (
        <div key={step} className="flex items-center gap-3">
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-all ${
              step < currentStep
                ? 'bg-brand-600 text-white'
                : step === currentStep
                ? 'border-2 border-brand-600 bg-brand-50 text-brand-600'
                : 'border-2 border-gray-200 bg-white text-gray-400'
            }`}
          >
            {step < currentStep ? <Check className="h-4 w-4" /> : step}
          </div>
          <span
            className={`text-sm ${
              step === currentStep
                ? 'font-medium text-gray-800'
                : 'text-gray-400'
            }`}
          >
            {step === 1 ? 'Tipo de conta' : 'Seus dados'}
          </span>
          {step < 2 && <div className="h-px w-6 bg-gray-200" />}
        </div>
      ))}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function RegisterPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<1 | 2>(1)
  const urlRole = searchParams.get('role') as Role | null
  const [selectedRole, setSelectedRole] = useState<Role | null>(
    urlRole === 'instructor' || urlRole === 'student' ? urlRole : null,
  )
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') ?? '')
  const [cpfRaw, setCpfRaw] = useState('')

  // CPF: obrigatório para instrutores, opcional para alunos
  const skipCpf = selectedRole !== 'instructor' && cpfRaw.replace(/\D/g, '').length === 0
  const cpfValidation = useCpfValidation(cpfRaw, skipCpf)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { acceptTerms: false },
  })

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role)
  }

  const handleStep1Continue = () => {
    if (!selectedRole) {
      toast.error('Selecione um tipo de conta para continuar.')
      return
    }
    setStep(2)
  }

  const onSubmit = async (data: RegisterFormData) => {
    if (!selectedRole) return

    // Validação de CPF antes de enviar
    if (selectedRole === 'instructor' && cpfRaw.replace(/\D/g, '').length === 0) {
      toast.error('CPF é obrigatório para instrutores.')
      return
    }
    if (cpfRaw.replace(/\D/g, '').length > 0 && !cpfValidation.valid) {
      toast.error(cpfValidation.message || 'CPF inválido.')
      return
    }

    const { data: signUpData, error: signUpError } =
      await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.full_name,
            phone: data.phone,
            role: selectedRole,
          },
        },
      })

    if (signUpError) {
      if (signUpError.message.includes('already registered')) {
        toast.error('Este e-mail já está cadastrado.', {
          description: 'Tente fazer login ou recuperar sua senha.',
        })
      } else {
        toast.error('Erro no cadastro', { description: signUpError.message })
      }
      return
    }

    const userId = signUpData.user?.id
    if (userId) {
      // Validate referral code if provided
      let referredBy: string | undefined
      const trimmedCode = referralCode.trim().toUpperCase()
      if (trimmedCode) {
        const { data: referrerId } = await supabase.rpc('validate_referral_code', {
          p_code: trimmedCode,
        })
        if (referrerId) referredBy = referrerId as string
      }

      // upsert instead of update: creates the row if the DB trigger didn't run
      // (e.g. schema was applied after this account was first created).
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: userId,
            full_name: data.full_name,
            phone: data.phone,
            role: selectedRole,
            ...(referredBy ? { referred_by: referredBy } : {}),
          },
          { onConflict: 'id' },
        )

      if (profileError) {
        Sentry.captureException(profileError, { tags: { source: 'RegisterPage.profileUpsert' } })
        if (import.meta.env.DEV) {
          console.error('Profile upsert error:', profileError)
        }
      }

      // Salva CPF hasheado se fornecido
      const cpfDigits = cpfRaw.replace(/\D/g, '')
      if (cpfDigits.length === 11) {
        const { error: cpfError } = await supabase.rpc('save_cpf', { p_cpf: cpfDigits })
        if (cpfError && import.meta.env.DEV) {
          console.warn('CPF save error:', cpfError.message)
        }
      }
    }

    track('signup_completed', { role: selectedRole })

    toast.success('Cadastro realizado com sucesso!', {
      description:
        selectedRole === 'instructor'
          ? 'Agora vamos configurar seu perfil de instrutor.'
          : 'Bem-vindo ao MeuDriver!',
    })

    if (selectedRole === 'instructor') {
      navigate('/instrutor/onboarding')
    } else {
      navigate('/aluno/dashboard')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="bg-brand-600 rounded-xl p-2">
            <Car className="h-6 w-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-gray-900">MeuDriver</span>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <ProgressIndicator currentStep={step} />
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {/* Step 1: Role selection */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  Como você quer usar o MeuDriver?
                </h2>
                <p className="mt-2 text-gray-500">
                  Escolha o tipo de conta que melhor te descreve.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RoleCard
                  role="student"
                  selected={selectedRole === 'student'}
                  onSelect={handleRoleSelect}
                />
                <RoleCard
                  role="instructor"
                  selected={selectedRole === 'instructor'}
                  onSelect={handleRoleSelect}
                />
              </div>

              <Button
                type="button"
                onClick={handleStep1Continue}
                className="w-full h-12 text-base font-semibold bg-brand-600 hover:bg-brand-700"
                disabled={!selectedRole}
              >
                Continuar
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Form */}
          {step === 2 && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="flex items-center gap-3 mb-6">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Seus dados
                  </h2>
                  <p className="text-sm text-gray-500">
                    Conta como{' '}
                    <span className="font-medium text-brand-600">
                      {selectedRole === 'instructor' ? 'instrutor' : 'aluno'}
                    </span>
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="full_name">Nome completo</Label>
                <Input
                  id="full_name"
                  placeholder="João da Silva"
                  className="h-11"
                  {...register('full_name')}
                />
                {errors.full_name && (
                  <p className="text-sm text-red-500">
                    {errors.full_name.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  className="h-11"
                  autoComplete="email"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="phone">Telefone / WhatsApp</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(11) 99999-9999"
                  className="h-11"
                  {...register('phone')}
                />
                {errors.phone && (
                  <p className="text-sm text-red-500">{errors.phone.message}</p>
                )}
              </div>

              {/* Campo CPF */}
              <div className="space-y-1.5">
                <Label htmlFor="cpf">
                  CPF{' '}
                  {selectedRole === 'instructor' ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-400 font-normal">(opcional)</span>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    id="cpf"
                    type="text"
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    className={`h-11 ${cpfRaw && !cpfValidation.valid && !cpfValidation.checking ? 'border-red-400' : cpfValidation.valid && cpfRaw ? 'border-green-400' : ''}`}
                    value={cpfRaw}
                    onChange={(e) => setCpfRaw(maskCpf(e.target.value))}
                    maxLength={14}
                    data-testid="cpf-input"
                  />
                  {cpfValidation.checking && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>
                {cpfRaw && !cpfValidation.checking && cpfValidation.message && (
                  <p className="text-sm text-red-500">{cpfValidation.message}</p>
                )}
                {cpfRaw && !cpfValidation.checking && cpfValidation.valid && (
                  <p className="text-sm text-green-600">CPF disponível.</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="referral-code">
                  Código de indicação{' '}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </Label>
                <Input
                  id="referral-code"
                  placeholder="REF-XXXXXXXX"
                  className="h-11 font-mono uppercase"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  maxLength={20}
                />
                {referralCode && (
                  <p className="text-xs text-green-600">
                    Você e quem te indicou ganharão créditos após sua primeira aula paga!
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="h-11 pr-11"
                      autoComplete="new-password"
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">Confirmar senha</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="h-11 pr-11"
                      autoComplete="new-password"
                      {...register('confirmPassword')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((p) => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showConfirm ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </div>

              <p className="text-xs text-gray-400">
                A senha deve ter pelo menos 8 caracteres, uma letra maiúscula e
                um número.
              </p>

              <div className="space-y-1.5">
                <label className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    {...register('acceptTerms')}
                  />
                  <span>
                    Li e aceito os{' '}
                    <Link
                      to="/termos"
                      target="_blank"
                      className="font-medium text-brand-600 hover:underline"
                    >
                      Termos de Uso
                    </Link>{' '}
                    e a{' '}
                    <Link
                      to="/privacidade"
                      target="_blank"
                      className="font-medium text-brand-600 hover:underline"
                    >
                      Política de Privacidade
                    </Link>
                    .
                  </span>
                </label>
                {errors.acceptTerms && (
                  <p className="text-sm text-red-500">
                    {errors.acceptTerms.message}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-brand-600 hover:bg-brand-700"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                {isSubmitting ? 'Criando conta...' : 'Criar conta grátis'}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          Já tem uma conta?{' '}
          <Link
            to="/login"
            className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
          >
            Faça login
          </Link>
        </p>
      </div>
    </div>
  )
}
